import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Prescription } from '@/types';
import { supabase } from '@/lib/supabase';
import { generatePrescriptionId } from '@/lib/utils/prescriptionId';

// Define a type for the prescription data required when creating a new prescription
export type NewPrescriptionData = Omit<Prescription, 'id' | 'createdAt' | 'status'>;

// Define status types for better type safety
export type PrescriptionStatus = 'draft' | 'issued';

function parseJsonField<T>(value: unknown, fallback: T): T {
  if (value == null) return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
}

export function mapPrescriptionFromDb(item: Record<string, unknown>): Prescription {
  return {
    id: String(item.id),
    patientId: String(item.patient_id ?? item.patientId),
    doctorId: String(item.doctor_id ?? item.doctorId),
    medications: parseJsonField(item.medications, []),
    notes: (item.notes as string) || '',
    chief_complaints: (item.chief_complaints as string) || '',
    complaints_duration: (item.complaints_duration as string) || '',
    clinical_findings: (item.clinical_findings as string) || '',
    diagnosis: (item.diagnosis as string) || '',
    lab_tests: parseJsonField(item.lab_tests, []),
    follow_up_date: (item.follow_up_date as string | null) ?? null,
    status: (item.status as string) || 'draft',
    instructions: (item.instructions as string) || '',
    vitals: parseJsonField(item.vitals, undefined),
    createdAt: String(item.created_at ?? item.createdAt),
    updatedAt: String(item.updated_at ?? item.updatedAt),
  };
}

// Define the store state interface for better type safety
interface PrescriptionsState {
  prescriptions: Prescription[];
  
  // Actions
  addPrescription: (prescription: NewPrescriptionData) => string;
  updatePrescription: (prescription: Prescription) => void;
  removePrescription: (id: string) => void;
  
  // Selectors
  getPrescriptionsByPatient: (patientId: string) => Prescription[];
  getPrescriptionsByDoctor: (doctorId: string) => Prescription[];
  getPrescriptionById: (id: string) => Prescription | undefined;
  // Fetch all prescriptions from Supabase
  fetchPrescriptions: () => Promise<void>;
}

export const usePrescriptionsStore = create<PrescriptionsState>()(
  persist(
    (set, get) => ({
      prescriptions: [],
      
      // Add a new prescription
      addPrescription: (prescriptionData) => {
        const currentPrescriptions = get().prescriptions;
        const id = generatePrescriptionId(currentPrescriptions);
        const newPrescription: Prescription = {
          ...prescriptionData,
          id,
          createdAt: new Date().toISOString()
        };
        
        set((state) => ({
          prescriptions: [...state.prescriptions, newPrescription]
        }));
        
        return id;
      },
      
      // Update an existing prescription
      updatePrescription: (updatedPrescription) => {
        set((state) => ({
          prescriptions: state.prescriptions.map((prescription) =>
            prescription.id === updatedPrescription.id ? updatedPrescription : prescription
          )
        }));
      },
      
      // Remove a prescription by ID
      removePrescription: (id) => {
        set((state) => ({
          prescriptions: state.prescriptions.filter((prescription) => prescription.id !== id)
        }));
      },
      
      // Get prescriptions by patient ID
      getPrescriptionsByPatient: (patientId) => {
        const { prescriptions } = get();
        return prescriptions
          .filter(prescription => prescription.patientId === patientId)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      },
      
      // Get prescriptions by doctor ID
      getPrescriptionsByDoctor: (doctorId) => {
        const { prescriptions } = get();
        return prescriptions
          .filter(prescription => prescription.doctorId === doctorId)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      },
      
      // Get a single prescription by ID
      getPrescriptionById: (id) => {
        const { prescriptions } = get();
        return prescriptions.find(prescription => prescription.id === id);
      },
      // Fetch all prescriptions from Supabase and update the store
      fetchPrescriptions: async () => {
        const { data, error } = await supabase
          .from('prescriptions')
          .select('*')
          .order('created_at', { ascending: false });
        if (error) throw error;
        set({
          prescriptions: (data || []).map((row) =>
            mapPrescriptionFromDb(row as Record<string, unknown>)
          ),
        });
      },
    }),
    {
      name: 'prescriptions-storage',
    }
  )
);

// Find existing draft for a patient
export async function findExistingDraft(patientId: string, doctorId: string) {
  try {
    
    const { data, error } = await supabase
      .from('prescriptions')
      .select('*')
      .eq('patient_id', patientId)
      .eq('doctor_id', doctorId)
      .eq('status', 'draft')
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        // No draft found (this is expected)
        return null;
      }
      console.error('Error finding existing draft:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in findExistingDraft:', error);
    throw error;
  }
}

// Update existing draft
export async function updateExistingDraft(draftId: string, prescription: Partial<Prescription>) {
  try {
    
    const updateData: any = {
      medications: JSON.stringify(prescription.medications || []),
      instructions: prescription.instructions || 'Draft prescription - instructions to be added',
      updated_at: new Date().toISOString(),
    };

    // Add status if provided (for converting draft to issued)
    if (prescription.status !== undefined) {
      updateData.status = prescription.status;
    }

    // Add optional fields - always include them to ensure they're saved even if empty
    updateData.notes = prescription.notes || '';
    updateData.chief_complaints = prescription.chief_complaints || '';
    updateData.complaints_duration = prescription.complaints_duration || '';
    updateData.clinical_findings = prescription.clinical_findings || '';
    updateData.diagnosis = prescription.diagnosis || '';
    updateData.lab_tests = JSON.stringify(prescription.lab_tests || []);
    if (prescription.follow_up_date !== undefined) updateData.follow_up_date = prescription.follow_up_date;
    if (prescription.vitals !== undefined) updateData.vitals = JSON.stringify(prescription.vitals);


    const { data, error } = await supabase
      .from('prescriptions')
      .update(updateData)
      .eq('id', draftId)
      .select();

    if (error) {
      console.error('Error updating draft:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in updateExistingDraft:', error);
    throw error;
  }
}

export async function savePrescriptionToBackend(prescription: Partial<Prescription>) {
  try {
    // Check if there's an existing draft for this patient/doctor combination
    const existingDraft = await findExistingDraft(prescription.patientId!, prescription.doctorId!);
    
    if (existingDraft) {
      
      // If we're converting a draft to issued, update the existing draft
      if (prescription.status === 'issued') {
        return await updateExistingDraft(existingDraft.id, {
          ...prescription,
          status: 'issued',
          updatedAt: new Date().toISOString()
        });
      } else {
        // If we're updating a draft, just update it
        console.log('Updating existing draft');
        return await updateExistingDraft(existingDraft.id, prescription);
      }
    }

    // No existing draft found, create a new prescription
    console.log('No existing draft found, creating new prescription');

    // Generate sequential ID for the prescription
    const { data: existingPrescriptions } = await supabase
      .from('prescriptions')
      .select('id, created_at')
      .order('created_at', { ascending: false });

    const prescriptionId = generatePrescriptionId(existingPrescriptions || []);

    // Create a minimal insert data object with only required fields
    const insertData: any = {
      id: prescriptionId,
      patient_id: prescription.patientId,
      doctor_id: prescription.doctorId,
      medications: JSON.stringify(prescription.medications || []),
      instructions: prescription.instructions || 'Draft prescription - instructions to be added',
      status: prescription.status || 'draft',
      created_at: prescription.createdAt || new Date().toISOString(),
      updated_at: prescription.updatedAt || new Date().toISOString(),
    };

    // Add optional fields only if they exist and have values
    if (prescription.notes) insertData.notes = prescription.notes;
    if (prescription.chief_complaints) insertData.chief_complaints = prescription.chief_complaints;
    if (prescription.complaints_duration) insertData.complaints_duration = prescription.complaints_duration;
    if (prescription.clinical_findings) insertData.clinical_findings = prescription.clinical_findings;
    if (prescription.diagnosis) insertData.diagnosis = prescription.diagnosis;
    if (prescription.lab_tests && prescription.lab_tests.length > 0) insertData.lab_tests = JSON.stringify(prescription.lab_tests);
    if (prescription.follow_up_date) insertData.follow_up_date = prescription.follow_up_date;
    if (prescription.vitals) insertData.vitals = JSON.stringify(prescription.vitals);

    console.log('Insert data for prescription:', {
      id: prescriptionId,
      status: prescription.status,
      medications: prescription.medications,
      medicationsJson: JSON.stringify(prescription.medications || [])
    });

    console.log('Saving prescription with ID:', prescriptionId);
    console.log('Final insert data:', insertData);

    try {
      const { data, error } = await supabase
        .from('prescriptions')
        .insert([insertData]);

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }
      
      console.log('Prescription saved successfully:', data);
      return data;
    } catch (dbError) {
      console.error('Database operation failed:', dbError);
      throw dbError;
    }
  } catch (error) {
    console.error('Error saving prescription:', error);
    throw error;
  }
}

// Test database connection
export async function testDatabaseConnection() {
  try {
    console.log('Testing database connection...');
    const { data, error } = await supabase
      .from('prescriptions')
      .select('count')
      .limit(1);
    
    if (error) {
      console.error('Database connection test failed:', error);
      return false;
    }
    
    console.log('Database connection test successful');
    return true;
  } catch (error) {
    console.error('Database connection test error:', error);
    return false;
  }
}

export async function fetchPrescriptionsByPatient(patientId: string) {
  try {
    console.log('Fetching prescriptions for patient:', patientId);
    
    const { data, error } = await supabase
      .from('prescriptions')
      .select('id, patient_id, doctor_id, medications, notes, chief_complaints, complaints_duration, clinical_findings, diagnosis, lab_tests, follow_up_date, status, created_at, updated_at')
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error('Error fetching prescriptions:', error);
      throw error;
    }
    
    console.log('Fetched prescriptions:', data);
    
    return (data || []).map((item) =>
      mapPrescriptionFromDb(item as Record<string, unknown>)
    );
  } catch (error) {
    console.error('Error in fetchPrescriptionsByPatient:', error);
    throw error;
  }
}
